import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, map, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IHttpSingleDataResponse } from '@irembo-andela/irembogov3-common';
import { IServiceChangeRequest } from '../models/service-change-request.model';
import { IService } from '../models/service.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { EServiceChangeRequestType } from '../models/service-change-request-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ActiveChangeRequestService {
  constructor(private http: HttpClient) {}

  getActiveCrId(
    changeRequest: IServiceChangeRequest
  ): Observable<string | null> {
    if (
      changeRequest.type !== EServiceChangeRequestType.SERVICE_UPDATE ||
      !changeRequest.serviceId
    ) {
      return of(null);
    }

    return this.http
      .get<IHttpSingleDataResponse<{ content: IService[] }>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/services?id=${changeRequest.serviceId}`
      )
      .pipe(
        map(response => response?.data?.content?.[0]?.changeRequestId ?? null),
        catchError(() => of(null))
      );
  }

  getActiveCrMetadata(
    activeCrId: string
  ): Observable<IServiceChangeRequest | null> {
    return this.http
      .get<IHttpSingleDataResponse<IServiceChangeRequest>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${activeCrId}`
      )
      .pipe(
        map(response => response?.data ?? null),
        catchError(() => of(null))
      );
  }

  getActiveCrFormDefinition(
    activeCrId: string
  ): Observable<Record<string, FormlyFieldConfig[]> | null> {
    return this.http
      .get<IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${activeCrId}/form-definition`
      )
      .pipe(
        map(response => response?.data ?? null),
        catchError(() => of(null))
      );
  }

  getActiveCrPricing(
    activeCrId: string
  ): Observable<Record<string, unknown> | null> {
    return this.http
      .get<IHttpSingleDataResponse<Record<string, unknown>>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${activeCrId}/pricing`
      )
      .pipe(
        map(response => response?.data ?? null),
        catchError(() => of(null))
      );
  }

  getActiveCrWorkflow(
    activeCrId: string
  ): Observable<Record<string, unknown> | null> {
    return this.http
      .get<IHttpSingleDataResponse<Record<string, unknown>>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${activeCrId}/workflow-next-steps-processing-time`
      )
      .pipe(
        map(response => response?.data ?? null),
        catchError(() => of(null))
      );
  }

  getActiveCrThirdPartyForm(
    activeCrId: string
  ): Observable<Record<string, unknown> | null> {
    return this.http
      .get<IHttpSingleDataResponse<Record<string, unknown>>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${activeCrId}/third-party-form-definition`
      )
      .pipe(
        map(response => response?.data ?? null),
        catchError(() => of(null))
      );
  }

  getActiveCrTranslation(
    activeCrId: string
  ): Observable<Record<string, Record<string, unknown>> | null> {
    return this.http
      .get<IHttpSingleDataResponse<Record<string, Record<string, unknown>>>>(
        `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${activeCrId}/translation`
      )
      .pipe(
        map(response => response?.data ?? null),
        catchError(() => of(null))
      );
  }
}
